import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextRecurringContract } from './RecurringContract';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { RecurringContractDA } from '../../../../services/admin/recurring.contract.service';
import { Invoice } from '../../../../models/invoice.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { legendInvalid, legendValid } from '../../../../utils/legend.data.util';
import { getError, getMessage } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { modalShow } from '../../../../utils/modal.util';

import { FaExclamationCircle } from 'react-icons/fa';

import AppInputContact from '../../../../components/element/InputContact';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputTreasuryAccount from '../../../../components/element/InputTreasuryAccount';
import AppInputLanguage from '../../../../components/element/InputLanguage';
import AppInputQrType from '../../../../components/element/InputQrType';
import AppSaleFormRequirements from '../component/SaleFormRequirements';
import AppModalInvoiceUpdateValidate from '../../../../components/modal/ModalInvoiceUpdateStatusValidate';
import AppPreloader from '../../../../components/Preloader';

import { foreignCurrencyDefault, foreignCurrencyPayload, isForeignCurrencyValid, ForeignCurrencyState } from '../../../../components/element/InputForeignCurrency';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { normalizeContactData } from '../../../../scripts/format.value.script';

export interface RecurringContractUpdatePageProps {};

let errorResponse: Error;

const RecurringContractUpdatePage: React.FunctionComponent<RecurringContractUpdatePageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, accountingAccountsForCompany, accountingIncomeAccountsForCompany, treasuryAccountsForCompany, contactsForCompany, productsForCompany, servicesForCompany, appServiceForUser, loadContactsForCompany} = UseOutletContextRecurringContract()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [contract, setContract] = useState<any | null>(null)
  const [customer, setCustomer] = useState({value: '', valid: false, object: null as any})
  const [title, setTitle] = useState({value: '', valid: false})
  const [description, setDescription] = useState({value: '', valid: true})
  const [startDate, setStartDate] = useState({value: '', valid: false})
  const [endDate, setEndDate] = useState({value: '', valid: true})
  const [cadenceUnit, setCadenceUnit] = useState({value: 'month', valid: true})
  const [cadenceInterval, setCadenceInterval] = useState({value: '1', valid: true})
  const [anchorDay, setAnchorDay] = useState({value: '1', valid: true})
  const [currency, setCurrency] = useState({value: '', valid: false, object: null as any})
  const [treasuryAccount, setTreasuryAccount] = useState({value: '', valid: false, object: null as any})
  const [language, setLanguage] = useState({value: '', valid: false})
  const [qrType, setQrType] = useState({value: 'qr-iban', valid: true})
  const [paymentTermsDays, setPaymentTermsDays] = useState({value: '30', valid: true})
  const [autoValidate, setAutoValidate] = useState(false)
  const [autoSend, setAutoSend] = useState(false)
  const [autoRenew, setAutoRenew] = useState(false)
  const [renewDaysBefore, setRenewDaysBefore] = useState({value: '0', valid: true})
  const [issueOffsetDays, setIssueOffsetDays] = useState({value: '0', valid: true})
  const [requirements, setRequirements] = useState({list: false, value: [] as {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string}[]}[], valid: [] as {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean}[]}[]})
  const [foreignCurrency, setForeignCurrency] = useState<ForeignCurrencyState>(foreignCurrencyDefault)
  const [fakeInvoice, setFakeInvoice] = useState<Invoice | null>(null)
  const [pendingData, setPendingData] = useState<any>(null)

  const round2 = (v: number): number => Math.round(v * 100) / 100

  const buildFakeInvoice = (reqs: any[]): Invoice => {
    const computed = reqs.map((req: any) => {
      const contents = (req.contents || []).map((line: any, idx: number) => {
        const taxRate = Number(line.tax_rate) || 0
        const quantity = Number(line.quantity) || 0
        const priceUnitRaw = Number(line.price_unit) || 0
        const taxIncluded = Boolean(line.tax_included)
        const priceUnit = taxIncluded ? priceUnitRaw / (1 + taxRate) : priceUnitRaw
        const rebate = line.rebate || {type: 'none', value: 0}
        let rebateAmount = 0
        if (rebate.type === 'percentage') rebateAmount = round2(priceUnit * (Number(rebate.value) / 100))
        else if (rebate.type === 'fixed') rebateAmount = round2(Number(rebate.value))
        const taxValue = round2((priceUnit * quantity - rebateAmount) * taxRate)
        const priceGross = round2(priceUnit * quantity)
        const priceNet = round2(priceUnit * quantity - rebateAmount + taxValue)
        return {
          ...line,
          id: `content-${req.type}-${idx}`,
          price_unit: priceUnitRaw,
          price_gross: priceGross,
          price_net: priceNet,
          tax_value: taxValue,
          rebate_amount: rebateAmount,
        }
      })
      const priceTotal = round2(contents.reduce((s: number, c: any) => s + c.price_net, 0))
      return {...req, contents, price_total: priceTotal}
    })
    return {requirements: computed, currency: currency.object ?? companyForUser?.currency} as any as Invoice
  }

  const executeUpdate = async (data: any) => {
    setLoadIndicator('on')
    try {
      const response = await RecurringContractDA.putRecurringContract(companyForUser!.id, param.id!, data)
      if (response.status === 200) {
        GlobalSwal.fire({
          title: getMessage(response.data?.message, lang.code) || lang.labels.successfullyUpdated,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then( () => {
          navigate('/main/sale/recurring-contract/view/' + param.id, {replace: true})
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoadIndicator('off')
    }
  }

  const handleCollectAccounts = (contentAccounts: {id_content: string, id_account_debit: string, id_account_credit: string}[]) => {
    if (!pendingData) return
    const data = {...pendingData}
    const reqs = [...(data.template.requirements || [])]
    const accountMap = new Map(contentAccounts.map(ca => [ca.id_content, ca.id_account_credit]))
    let idx = 0
    for (const req of reqs) {
      for (const content of (req.contents || [])) {
        const contentId = `content-${req.type}-${idx}`
        if (accountMap.has(contentId)) {
          content.id_account = accountMap.get(contentId)
        }
        idx++
      }
    }
    data.template.requirements = reqs
    setPendingData(null)
    setFakeInvoice(null)
    executeUpdate(data)
  }

  const loadContract = async (id_company: string, id_contract: string) => {
    try {
      const response = await RecurringContractDA.getRecurringContract(id_company, id_contract)
      if (response.status === 200) {
        const data = response.data
        if (data.contact) normalizeContactData(data.contact)

        setContract(data)

        // Map contact: use id_contact for value, contact snapshot for display
        const contactId = data.id_contact ?? data.contact?.data?.id ?? data.contact?.id ?? ''
        // For InputContact, find the matching contact from the list to set as object
        const matchedContact = contactsForCompany?.find((c: any) => c.id === contactId) ?? data.contact
        setCustomer({value: contactId, valid: !!contactId, object: matchedContact})

        setTitle({value: data.label ?? data.template?.concerning ?? '', valid: !!(data.label || data.template?.concerning)})
        setDescription({value: data.template?.concerning ?? '', valid: true})
        setStartDate({value: data.start_date ? data.start_date.substring(0, 10) : '', valid: !!data.start_date})
        setEndDate({value: data.end_date ? data.end_date.substring(0, 10) : '', valid: true})

        // Cadence
        setCadenceUnit({value: data.cadence?.unit ?? 'month', valid: true})
        setCadenceInterval({value: String(data.cadence?.interval ?? 1), valid: true})
        setAnchorDay({value: String(data.cadence?.anchor_day ?? 1), valid: true})

        // Template fields
        const tpl = data.template ?? {}
        setLanguage({value: tpl.language ?? '', valid: !!tpl.language})
        setQrType({value: tpl.qr_type ?? 'qr-iban', valid: true})
        setPaymentTermsDays({value: String(tpl.payment_terms_days ?? 30), valid: true})

        if (tpl.currency) {
          setCurrency({value: tpl.currency.code ?? '', valid: true, object: tpl.currency})
        }
        if (tpl.treasury_account) {
          const taId = typeof tpl.treasury_account === 'string' ? tpl.treasury_account : tpl.treasury_account.id
          const matchedTa = treasuryAccountsForCompany?.find((ta: any) => ta.id === taId)
          setTreasuryAccount({value: taId ?? '', valid: !!taId, object: matchedTa ?? tpl.treasury_account})
        }

        // Toggles
        setAutoValidate(!!data.auto_validate)
        setAutoSend(!!data.auto_send)
        setAutoRenew(!!data.auto_renew)
        setRenewDaysBefore({value: String(data.renew_days_before ?? 0), valid: true})
        setIssueOffsetDays({value: String(data.issue_offset_days ?? 0), valid: true})

        const reqs = tpl.requirements ?? []
        if (reqs.length > 0) {
          let tempRequirementsValue: any[] = []
          let tempRequirementsValid: any[] = []

          for (let requirement of reqs) {
            let tempContentsValue: any[] = []
            let tempContentsValid: any[] = []

            for (let content of (requirement.contents ?? [])) {
              tempContentsValue.push({
                detail: content.detail ?? '',
                reference: content.reference ?? '',
                quantity: content.quantity ?? '',
                price_unit: content.price_unit ?? '',
                tax_included: content.tax_included ?? false,
                tax_code: content.tax_code ?? '',
                tax_rate: content.tax_rate ?? 0,
                rebate_type: content.rebate_type ?? 'none',
                rebate_value: content.rebate_value ?? 0,
                rebate_quantity: content.rebate_quantity ?? 0,
                rebate_amount: content.rebate_amount ?? '0',
                unit: content.unit ?? ''
              })
              tempContentsValid.push({
                detail: !!(content.detail),
                quantity: !!(content.quantity),
                price_unit: !!(content.price_unit),
                tax: true,
                rebate_amount: true,
                unit: true
              })
            }

            tempRequirementsValue.push({
              type: requirement.type ?? 'item',
              description: requirement.description ?? '',
              contents: tempContentsValue
            })
            tempRequirementsValid.push({
              type: true,
              description: true,
              content: tempContentsValue.length > 0,
              contents: tempContentsValid
            })
          }

          setRequirements({list: true, value: tempRequirementsValue, valid: tempRequirementsValid})
        }
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        }).then(() => {
          navigate('/main/sale/recurring-contract/list', {replace: true})
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const handleChangeTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle({value: event.target.value, valid: event.target.value.length > 0})
    if (event.target.value.length > 0) { legendValid('legend-title-required') } else { legendInvalid('legend-title-required') }
  }

  const handleChangeDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription({value: event.target.value, valid: true})
  }

  const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate({value: event.target.value, valid: event.target.value.length > 0})
    if (event.target.value.length > 0) { legendValid('legend-start-date-required') } else { legendInvalid('legend-start-date-required') }
  }

  const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate({value: event.target.value, valid: true})
  }

  const handleChangeCadenceUnit = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCadenceUnit({value: event.target.value, valid: true})
  }

  const handleChangeCadenceInterval = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    if (raw === '' || /^\d+$/.test(raw)) {
      const num = raw === '' ? NaN : parseInt(raw, 10)
      setCadenceInterval({value: raw, valid: raw !== '' && num >= 1})
    }
  }

  const handleChangeAnchorDay = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    if (raw === '' || /^\d+$/.test(raw)) {
      const num = raw === '' ? NaN : parseInt(raw, 10)
      setAnchorDay({value: raw, valid: raw !== '' && num >= 1 && num <= 31})
    }
  }

  const handleChangePaymentTermsDays = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    if (raw === '' || /^\d+$/.test(raw)) {
      const num = raw === '' ? NaN : parseInt(raw, 10)
      setPaymentTermsDays({value: raw, valid: raw !== '' && num >= 0})
    }
  }

  const handleChangeIssueOffsetDays = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    if (raw === '' || /^\d+$/.test(raw)) {
      const num = raw === '' ? NaN : parseInt(raw, 10)
      setIssueOffsetDays({value: raw, valid: raw !== '' && num >= 0})
    }
  }

  const handleChangeRenewDaysBefore = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    if (raw === '' || /^\d+$/.test(raw)) {
      const num = raw === '' ? NaN : parseInt(raw, 10)
      setRenewDaysBefore({value: raw, valid: raw !== '' && num >= 0})
    }
  }

  const submitUpdate = async () => {
    setLoadIndicator('on')

    let requerimentsIsValid: boolean = requirements.valid.every((item) => isValidObjectAttributes(item))

    if (companyForUser && customer.valid && title.valid && startDate.valid && currency.valid && treasuryAccount.valid && language.valid && requirements.list && requerimentsIsValid && isForeignCurrencyValid(foreignCurrency)) {
      let companyData = {uuid: companyForUser.uuid, name: companyForUser.name, logo: companyForUser.logo, address: companyForUser.address, phone: companyForUser.phone || '', email: companyForUser.email || '', website: companyForUser.website || '', vat_number: companyForUser.vat_number || ''}

      let data: any = {
        label: title.value,
        id_contact: customer.object?.id,
        template: {
          concerning: description.value,
          currency: currency.object ?? companyForUser?.currency,
          treasury_account: treasuryAccount.object,
          requirements: requirements.value,
          qr_type: qrType.value,
          language: language.value,
          payment_terms_days: Math.max(parseInt(paymentTermsDays.value, 10) || 0, 0),
          company: companyData,
        },
        cadence: {
          unit: cadenceUnit.value,
          interval: Math.max(parseInt(cadenceInterval.value, 10) || 1, 1),
          anchor_day: Math.min(Math.max(parseInt(anchorDay.value, 10) || 1, 1), 31),
        },
        start_date: startDate.value,
        end_date: endDate.value || null,
        issue_offset_days: Math.max(parseInt(issueOffsetDays.value, 10) || 0, 0),
        auto_validate: autoValidate,
        auto_send: autoSend,
        auto_renew: autoRenew,
        renew_days_before: autoRenew ? Math.max(parseInt(renewDaysBefore.value, 10) || 0, 0) : 0,
      }

      if (autoValidate) {
        setPendingData(data)
        setFakeInvoice(buildFakeInvoice(requirements.value))
        setLoadIndicator('off')
        setTimeout(() => modalShow('modal-invoice-collect-accounts'), 100)
        return
      }

      await executeUpdate(data)
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!customer.value) legendInvalid('legend-contact-required')
        if (!title.value) legendInvalid('legend-title-required')
        if (!startDate.value) legendInvalid('legend-start-date-required')
        if (!currency.value) legendInvalid('legend-currency-required')
        if (!treasuryAccount.value) legendInvalid('legend-treasury-account-required')
        if (!language.value) legendInvalid('legend-language-required')
      })

      setLoadIndicator('off')
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadContract(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.recurringContracts || 'Recurring contracts', branch: lang.labels.updateRecurringContract || 'Update recurring contract'}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && contactsForCompany && treasuryAccountsForCompany && productsForCompany && servicesForCompany && contract
        ?
        <>
        <div className="card card-flush mb-5">
          <div className="card-header">
            <h3 className="card-title fw-bolder">{lang.labels.updateRecurringContract || 'Update recurring contract'}</h3>
          </div>
          <div className="card-body pt-0">
            <div className="row mb-5">
              <div className="col-12">
                <label className="form-label required">{lang.labels.customer}</label>
                <AppInputContact appServiceForUser={appServiceForUser} companyForUser={companyForUser} settingsForCompany={settingsForCompany} contact={customer} setContact={setCustomer} setLanguage={setLanguage} setQrTtype={setQrType} loadContactsForCompany={loadContactsForCompany}></AppInputContact>
                <div id="legend-contact-required" className="fv-plugins-message-container invalid-feedback d-none">{lang.labels.required}</div>
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-12">
                <label className="form-label required">{lang.labels.title || 'Title'}</label>
                <input className="form-control" type="text" name="title" autoComplete="off" value={title.value} onChange={handleChangeTitle} />
                <div id="legend-title-required" className="fv-plugins-message-container invalid-feedback d-none">{lang.labels.required}</div>
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-md-12">
                <label className="form-label">{lang.labels.description}</label>
                <textarea className="form-control" rows={3} value={description.value} onChange={handleChangeDescription}></textarea>
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-md-3">
                <label className="form-label required">{lang.labels.startDate || 'Start date'}</label>
                <input className="form-control" type="date" value={startDate.value} onChange={handleChangeStartDate} />
                <div id="legend-start-date-required" className="fv-plugins-message-container invalid-feedback d-none">{lang.labels.required}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label">{lang.labels.endDate || 'End date'}</label>
                <input className="form-control" type="date" value={endDate.value} onChange={handleChangeEndDate} />
              </div>
              <div className="col-md-2">
                <label className="form-label required">{lang.labels.frequency || 'Frequency'}</label>
                <select className="form-select" value={cadenceUnit.value} onChange={handleChangeCadenceUnit}>
                  <option value="month">{lang.labels.monthly || 'Monthly'}</option>
                  <option value="quarter">{lang.labels.quarterly || 'Quarterly'}</option>
                  <option value="year">{lang.labels.annual || 'Annual'}</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">{lang.labels.interval || 'Interval'}</label>
                <input className="form-control" type="number" min="1" value={cadenceInterval.value} onChange={handleChangeCadenceInterval} />
              </div>
              <div className="col-md-2">
                <label className="form-label">{lang.labels.anchorDay || 'Day of month'}</label>
                <input className="form-control" type="number" min="1" max="31" value={anchorDay.value} onChange={handleChangeAnchorDay} />
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-md-3">
                <label className="form-label">
                  <span className="required">{lang.labels.language}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultLanguageToDownloadIsTheClients}><FaExclamationCircle /></i>
                </label>
                <AppInputLanguage language={language} setLanguage={setLanguage}></AppInputLanguage>
                <div id="legend-language-required" className="fv-plugins-message-container invalid-feedback d-none">{lang.labels.required}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  <span className="required">{lang.labels.currency}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultCurrencyIsTheCompanys}><FaExclamationCircle /></i>
                </label>
                <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
                <div id="legend-currency-required" className="fv-plugins-message-container invalid-feedback d-none">{lang.labels.required}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  <span className="required">{lang.labels.treasuryAccount}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultBankAccountIsTheCompanys}><FaExclamationCircle /></i>
                </label>
                <AppInputTreasuryAccount solid={true} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount}></AppInputTreasuryAccount>
                <div id="legend-treasury-account-required" className="fv-plugins-message-container invalid-feedback d-none">{lang.labels.required}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  <span className="required">{lang.labels.qrType}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultShapeOrVersionOfAQrInvoiceToDownloadIsTheClients}><FaExclamationCircle /></i>
                </label>
                <AppInputQrType qrType={qrType} setQrType={setQrType}></AppInputQrType>
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-md-3">
                <label className="form-label">{lang.labels.paymentTermsDays || 'Payment terms (days)'}</label>
                <input className="form-control" type="number" min="0" value={paymentTermsDays.value} onChange={handleChangePaymentTermsDays} />
              </div>
              <div className="col-md-3">
                <label className="form-label">{lang.labels.issueOffsetDays || 'Issue offset (days)'}</label>
                <input className="form-control" type="number" min="0" value={issueOffsetDays.value} onChange={handleChangeIssueOffsetDays} />
              </div>
              <div className="col-md-3 d-flex align-items-end pb-2">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={autoValidate} onChange={(e) => setAutoValidate(e.target.checked)} />
                  <label className="form-check-label">{lang.labels.autoValidate || 'Auto validate'}</label>
                </div>
              </div>
              <div className="col-md-3 d-flex align-items-end pb-2">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} />
                  <label className="form-check-label">{lang.labels.autoSend || 'Auto send'}</label>
                </div>
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-md-3 d-flex align-items-end pb-2">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
                  <label className="form-check-label">{lang.labels.autoRenew || 'Auto renew'}</label>
                </div>
              </div>
              { autoRenew &&
                <div className="col-md-3">
                  <label className="form-label">{lang.labels.renewDaysBefore || 'Renew days before end'}</label>
                  <input className="form-control" type="number" min="0" value={renewDaysBefore.value} onChange={handleChangeRenewDaysBefore} />
                </div>
              }
            </div>
          </div>
        </div>
        <AppSaleFormRequirements date={startDate.value || new Date().toISOString().slice(0, 10)} currency={currency.object ?? companyForUser?.currency} requirements={requirements} setRequirements={setRequirements}></AppSaleFormRequirements>
        <div className="card card-flush">
          <div className="card-footer d-flex justify-content-end py-6 px-9">
            <button className="btn btn-light me-3" type="button" onClick={() => navigate('/main/sale/recurring-contract/view/' + param.id)}>{lang.labels.discard}</button>
            <button className="btn btn-primary" type="button" disabled={loadIndicator === 'on'} onClick={submitUpdate}>
              {loadIndicator === 'off' && <span className="indicator-label">{lang.labels.update}</span>}
              {loadIndicator === 'on' && <span className="indicator-progress d-block">{lang.labels.pleaseWait}<span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>}
            </button>
          </div>
        </div>
        <AppModalInvoiceUpdateValidate companyForUser={companyForUser} settingsForCompany={settingsForCompany} accountingAccountsForCompany={accountingAccountsForCompany} accountingIncomeAccountsForCompany={accountingIncomeAccountsForCompany} servicesForCompany={servicesForCompany} productsForCompany={productsForCompany} invoice={fakeInvoice} setInvoice={setFakeInvoice} mode="collect" onCollect={handleCollectAccounts}></AppModalInvoiceUpdateValidate>
        </>
        :
        <AppPreloader/>
      }
    </div>
  );
}

export default RecurringContractUpdatePage;
